/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Typography,
  TextField,
  Box,
  Paper,
  List,
  ListSubheader,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { X } from 'react-feather';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import InfoIcon from '@mui/icons-material/Info';
import {
  selectIsDemoModeActivated,
  selectSameStoreComparisonDate,
} from '../../../dashboard/selectors';
import { isEmpty } from '../../helpers/object';
import CustomToggleSwitch from '../toggle-switch';
import { INFINITE_DATE, YEAR_MONTH_DAY_PATTERN } from '../../constants/date-range';

/**
 * ViewHotelSetOverTime modal component
 * Displays a dialog showing a list of hotels and their status over a selected date range.
 * @param {boolean} props.open - Controls whether the dialog is open
 * @param {Function} props.onClose - Callback triggered when dialog is closed
 * @param {Object} props.selectedHotels - Selected hotel set metadata
 * @param {Array} props.invalidPrimaryHotels - List of excluded (invalid) hotels
 * @param {boolean} props.hotelSetOverTime - Whether "Same Set" toggle is active
 * @param {Function} props.handleHotelSetChange - Callback for toggle state change
 */

const ViewHotelSetOverTime = ({
  selectedHotels,
  invalidPrimaryHotels,
  hotelSetOverTime,
  open,
  onClose,
  handleHotelSetChange,
}) => {
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const sameStoreComparisonDate = useSelector(selectSameStoreComparisonDate);
  //
  const excludedHotelIds = new Set(invalidPrimaryHotels?.map((h) => h?.id));
  const validHotels = selectedHotels?.hotels?.filter((hotel) => !excludedHotelIds.has(hotel?.id));
  const excludedHotels = selectedHotels?.hotels?.filter((hotel) => excludedHotelIds.has(hotel?.id));
  //
  useEffect(() => {
    if (!validHotels?.length) {
      handleHotelSetChange({ target: { checked: false } });
    }
  }, [validHotels]);
  //
  const renderHotelList = (items, header) => (
    <Paper
      sx={{
        width: '100%',
        height: 350,
        overflow: 'auto',
      }}
      variant="outlined"
    >
      <List
        dense
        component="div"
        role="list"
        subheader={
          <ListSubheader component="div" id={`list-subheader-${header}`}>
            {header}
          </ListSubheader>
        }
      >
        {isEmpty(items) ? (
          <ListItem role="listitem" sx={{ padding: 1, justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No hotels to display
            </Typography>
          </ListItem>
        ) : (
          items?.map((value) => {
            const isInvalid = excludedHotelIds?.has(value?.id);
            const maxDateFormatted = value?.dataAvailableMaxDate
              ? format(new Date(value.dataAvailableMaxDate), YEAR_MONTH_DAY_PATTERN)
              : null;
            const displayMaxDate =
              maxDateFormatted === INFINITE_DATE.date
                ? INFINITE_DATE.label
                : maxDateFormatted || 'N/A';
            return (
              <ListItem key={value.id} role="listitem" sx={{ padding: 0 }}>
                <ListItemIcon sx={{ paddingLeft: 3, minWidth: 35 }}>
                  <FiberManualRecordIcon color={isInvalid ? 'error' : 'disabled'} />
                </ListItemIcon>
                <ListItemText
                  id={value?.id}
                  primary={
                    <Typography
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        fontSize: 14,
                      }}
                    >
                      {isDemoModeActivated ? value?.altName : value?.name}
                      {value?.dataAvailableMinDate
                        ? format(new Date(value.dataAvailableMinDate), YEAR_MONTH_DAY_PATTERN)
                        : '  N/A'}
                      {' to '}
                      {displayMaxDate}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })
        )}
      </List>
    </Paper>
  );
  //
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '1300px',
            maxHeight: '90vh',
          },
        },
      }}
    >
      <DialogTitle sx={{ paddingBottom: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Hotel Sets - Same Store</Typography>
          <IconButton
            size="small"
            aria-label="close"
            onClick={onClose}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <X />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          name="hotelName"
          value={selectedHotels?.id}
          InputLabelProps={{ shrink: true }}
          fullWidth
          label="Hotel Set"
          size="small"
          sx={{ my: 4 }}
        />
        <Grid container alignItems="center">
          <InfoIcon color="primary" />
          <Typography sx={{ ml: 2 }}>
            {sameStoreComparisonDate &&
              `The red marked hotels do not have complete data for your selected date range (
            ${sameStoreComparisonDate?.startDate} – ${sameStoreComparisonDate?.endDate}
            ). Consider a same-store view.`}
          </Typography>
        </Grid>
        <Box
          sx={{
            borderRadius: '8px',
            borderColor: '#8c8787 !important',
            border: 1,
            padding: 4,
            mt: 3,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" mb={2}>
              Hotel Details
            </Typography>
            <Grid>
              <CustomToggleSwitch
                label="Same Store"
                title=""
                disabled={!validHotels?.length}
                onChange={handleHotelSetChange}
                checked={hotelSetOverTime}
              />
              <Tooltip
                title={
                  !validHotels?.length
                    ? 'Same store view is not available when all the hotels do not have complete data'
                    : 'When selected, hotels not available throughout the selected date range will be excluded.'
                }
              >
                <InfoIcon color="primary" sx={{ mt: 1.2 }} />
              </Tooltip>
            </Grid>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {renderHotelList(hotelSetOverTime ? validHotels : selectedHotels?.hotels, 'Hotels')}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderHotelList(hotelSetOverTime ? excludedHotels : [], 'Excluded Hotels')}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
//
export default ViewHotelSetOverTime;
